// Shape-aware renderer for metric results.
//
// The library returns a different typed result per function, so rather than a
// component per function this walks the payload and renders by shape: rates get
// a headline card, findings get the alert treatment, lists of objects become
// tables, and anything left over lands in a key/value summary.
import React from 'react';
import styles from '../MetricsPlayground.module.css';

const CURRENCY = '₹';

// A RateResult is recognisable by its low-data guard plus its numerator.
const isRate = (value) =>
  value !== null &&
  typeof value === 'object' &&
  !Array.isArray(value) &&
  'sufficient_data' in value &&
  'numerator' in value;

const formatNumber = (value) =>
  Number.isInteger(value) ? value.toLocaleString('en-IN') : value.toFixed(2);

const formatCell = (value) => {
  if (value === null || value === undefined) return <span className={styles.muted}>—</span>;
  if (typeof value === 'boolean') return value ? 'yes' : 'no';
  if (typeof value === 'number') return formatNumber(value);
  if (Array.isArray(value)) {
    return value.length ? value.join(', ') : <span className={styles.muted}>—</span>;
  }
  if (typeof value === 'object') return <code className={styles.inlineCode}>{JSON.stringify(value)}</code>;
  return String(value);
};

const severityClass = (severity) =>
  severity === 'CRITICAL' ? styles.critical : severity === 'WARN' ? styles.warn : styles.info;

// The low-data state is rendered as its own badge. Showing 0% below the volume
// floor is exactly the misleading number the floor exists to prevent.
const RateCard = ({ rate }) => (
  <section className={styles.card}>
    <h3 className={styles.cardTitle}>{rate.label || rate.metric}</h3>
    <div className={styles.rateRow}>
      {rate.sufficient_data ? (
        <span className={styles.rateValue}>{rate.value === null ? '—' : `${rate.value}%`}</span>
      ) : (
        <span className={`${styles.badge} ${styles.badgeLow}`}>insufficient data</span>
      )}
      {rate.estimated && <span className={`${styles.badge} ${styles.badgeWarn}`}>estimated</span>}
    </div>
    <dl className={styles.kv}>
      <dt>numerator</dt><dd>{formatNumber(rate.numerator)}</dd>
      <dt>denominator</dt><dd>{formatNumber(rate.denominator)}</dd>
      {rate.window && (
        <>
          <dt>window</dt><dd>{rate.window.start} → {rate.window.end}</dd>
        </>
      )}
      {rate.excludes?.length > 0 && (
        <>
          <dt>excludes</dt><dd>{rate.excludes.join(', ')}</dd>
        </>
      )}
    </dl>
    {rate.notes?.map((note) => (
      <p key={note} className={styles.note}>· {note}</p>
    ))}
  </section>
);

// Findings are the one shape rankActions will consume, so they are rendered the
// same way regardless of which function produced them.
const FindingList = ({ findings }) => {
  if (!findings?.length) return null;
  return (
    <section className={styles.card}>
      <h3 className={styles.cardTitle}>Findings — what rankActions would consume</h3>
      {findings.map((finding) => (
        <article
          key={`${finding.code}-${finding.entity_id}`}
          className={`${styles.finding} ${severityClass(finding.severity)}`}
        >
          <header className={styles.findingHead}>
            <span className={`${styles.badge} ${severityClass(finding.severity)}`}>
              {finding.severity}
            </span>
            {finding.headline}
          </header>
          <p className={styles.findingMeta}>
            {finding.entity_type} {finding.entity_id} · revenue at risk{' '}
            <strong>{CURRENCY}{formatNumber(finding.revenue_at_risk)}</strong> ·{' '}
            <code className={styles.inlineCode}>{finding.code}</code>
          </p>
          {finding.detail && <p className={styles.findingDetail}>{finding.detail}</p>}
          {finding.recommended_action && (
            <p className={styles.note}>→ {finding.recommended_action}</p>
          )}
        </article>
      ))}
    </section>
  );
};

const ROW_LIMIT = 60;

const RowTable = ({ title, rows }) => {
  if (!rows.length) {
    return (
      <section className={styles.card}>
        <h3 className={styles.cardTitle}>{title}</h3>
        <p className={styles.muted}>No rows.</p>
      </section>
    );
  }
  const columns = [...new Set(rows.flatMap(Object.keys))].filter((c) => c !== 'findings');
  const visible = rows.slice(0, ROW_LIMIT);

  return (
    <section className={styles.card}>
      <h3 className={styles.cardTitle}>{title} — {rows.length} rows</h3>
      <div className={styles.tableScroll}>
        <table className={styles.table}>
          <thead>
            <tr>{columns.map((c) => <th key={c}>{c}</th>)}</tr>
          </thead>
          <tbody>
            {visible.map((row, index) => (
              <tr key={index}>
                {columns.map((column) => {
                  const value = row[column];
                  if (isRate(value)) {
                    return (
                      <td key={column} className={styles.numeric}>
                        {value.sufficient_data
                          ? `${value.value ?? '—'}%`
                          : <span className={`${styles.badge} ${styles.badgeLow}`}>low data</span>}
                      </td>
                    );
                  }
                  return (
                    <td key={column} className={typeof value === 'number' ? styles.numeric : undefined}>
                      {formatCell(value)}
                    </td>
                  );
                })}
              </tr>
            ))}
          </tbody>
        </table>
      </div>
      {rows.length > ROW_LIMIT && (
        <p className={styles.muted}>Showing the first {ROW_LIMIT} of {rows.length}.</p>
      )}
    </section>
  );
};

// A dict of RateResults (buy box by ASIN, cancellations by channel) reads far
// better as a table than as a stack of cards.
const rateMapToRows = (map) =>
  Object.entries(map).map(([key, rate]) => ({
    key,
    value: rate.sufficient_data ? rate.value : null,
    numerator: rate.numerator,
    denominator: rate.denominator,
    sufficient_data: rate.sufficient_data,
  }));

const isRateMap = (value) => {
  if (!value || typeof value !== 'object' || Array.isArray(value)) return false;
  const inner = Object.values(value);
  return inner.length > 0 && inner.every(isRate);
};

const ResultView = ({ result, raw }) => {
  if (result === null || result === undefined) return null;

  if (raw) {
    return (
      <section className={styles.card}>
        <h3 className={styles.cardTitle}>Raw result</h3>
        <pre className={styles.pre}>{JSON.stringify(result, null, 2)}</pre>
      </section>
    );
  }

  if (Array.isArray(result)) return <RowTable title="result" rows={result} />;
  if (typeof result !== 'object') return <pre className={styles.pre}>{String(result)}</pre>;
  if (isRate(result)) return <RateCard rate={result} />;

  const blocks = [];
  const scalars = [];

  Object.entries(result).forEach(([key, value]) => {
    if (key === 'findings') {
      blocks.push(<FindingList key={key} findings={value} />);
    } else if (isRate(value)) {
      blocks.push(<RateCard key={key} rate={value} />);
    } else if (Array.isArray(value) && value.length > 0 && typeof value[0] === 'object') {
      blocks.push(<RowTable key={key} title={key} rows={value} />);
    } else if (isRateMap(value)) {
      blocks.push(<RowTable key={key} title={key} rows={rateMapToRows(value)} />);
    } else {
      scalars.push([key, value]);
    }
  });

  return (
    <>
      {scalars.length > 0 && (
        <section className={styles.card}>
          <h3 className={styles.cardTitle}>Summary</h3>
          <dl className={styles.kv}>
            {scalars.map(([key, value]) => (
              <React.Fragment key={key}>
                <dt>{key}</dt><dd>{formatCell(value)}</dd>
              </React.Fragment>
            ))}
          </dl>
        </section>
      )}
      {blocks}
    </>
  );
};

export default ResultView;
